/**
 * Document Management ▸ Documents — the published-document reader.
 *
 * BO UI Standard **Class E** (read-only / analytics): a grid and nothing that mutates —
 * no New, no Edit, no Delete. Authoring lives on Settings ▸ Document Management ▸
 * Document Creator.
 *
 * ── Step Island, by owner decision (2026-10-12) ──────────────────────────────
 * The standard gives Class E NO stepper (§2 class table; §3 "screens with no
 * stepper leave the top-bar centre EMPTY") because a reader has no workflow to
 * progress through. The owner asked for one here anyway, for consistency with
 * the screens that do have one — a SANCTIONED DEVIATION, recorded so the next
 * reader does not "fix" it back. It is the SHARED <TopbarStepper>, never a
 * bespoke pill, and its three states are computed from REAL page state (which
 * category, which row, whether that row's file has been opened) — no step is
 * ever marked done just because the page loaded.
 *
 * ── Why a category rail instead of per-category menu items ───────────────────
 * ALPACA has no "Documents" form. Its nav node carries no ViewId; a child is GENERATED
 * at runtime for every DocumentCategory that holds a readable document, and each child
 * opens the same view re-filtered to the clicked category (the category reaching the
 * view through XAF session state). G6 replaces that with ONE nav entry and this rail —
 * settled by data: the generated rows exist on the customer DB but 19 roles hold 0
 * grants against them, so per-category permissions were never used. The rail also
 * deletes two legacy defects outright (the role loop that stops after the first role;
 * the category NAME used as a permission key, so a rename broke the grant).
 *
 * ── No hint bar ──────────────────────────────────────────────────────────────
 * The BO UI Standard retired the step-hint / form-guidance strip on 2026-09-15 and
 * explicitly forbids re-introducing one, and §4 forbids a descriptive paragraph under
 * the title. Anything the user must be told surfaces through `useNotify` at the moment
 * it matters, not as standing furniture.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppDialog } from '../../components/AppDialog';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useNotify } from '../../components/notifications';
import { ProjectNotesButton } from '../../components/ProjectNotes';
import { type IslandStep } from '../../components/StepIsland/StepIsland';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import { documentsApi, type PublishedCategory, type PublishedDocument } from './documentsApi';
// `.dp-breadcrumb` is a SHARED class that lives in backlog.css, so import it here
// rather than relying on another page having loaded it. Today the app does not
// code-split, so it happens to be present either way — that is luck, not a
// contract, and the day a route is lazy-loaded the crumb would render unstyled.
import '../backlog/backlog.css';
import './DocumentsPage.css';

export interface DocumentsPageProps {
  /** Navigate Home from the breadcrumb. */
  onHome?: () => void;
  /** The DocumentCategory this page was opened on, from the `documents:<oid>`
   *  nav leaf. Null = opened on the plain Documents entry, so the first category
   *  wins as before. */
  initialCategoryId?: string | null;
}

/** What the Step Island reads. Kept as plain data so the rule below is testable
 *  without rendering the page. */
export interface DocumentStepState {
  /** The open category, if any. */
  activeCat: string | null;
  /** The highlighted row, if any. */
  selected: string | null;
  /** Is the viewer dialog open right now? */
  viewing: boolean;
  /** The document whose file was actually opened, if any. */
  viewedId: string | null;
}

/**
 * The three Step Island states, derived from REAL page state — never from the
 * page merely having loaded. A pure function so the rule can be pinned by a
 * test: the whole justification for putting a stepper on a Class E screen
 * (owner deviation, see the file header) is that it reports the truth.
 *
 * View is 'done' only for the row whose file was opened, so highlighting a
 * different document correctly drops it back to 'active'.
 */
export function documentSteps(
  { activeCat, selected, viewing, viewedId }: DocumentStepState,
): IslandStep[] {
  const view: IslandStep['state'] =
    viewing ? 'active'
    : viewedId && viewedId === selected ? 'done'
    : selected ? 'active'
    : 'todo';
  return [
    { id: 'category', label: 'Select Category', state: activeCat ? 'done' : 'active' },
    { id: 'document', label: 'Select Document', state: selected ? 'done' : activeCat ? 'active' : 'todo' },
    { id: 'view',     label: 'View',            state: view },
  ];
}

export function DocumentsPage({ onHome, initialCategoryId = null }: DocumentsPageProps = {}) {
  const notify = useNotify();

  const [categories, setCategories] = useState<PublishedCategory[]>([]);
  const [activeCat, setActiveCat] = useState<string | null>(null);
  const [rows, setRows] = useState<PublishedDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  // The document whose file was actually OPENED (not merely highlighted) — the
  // only honest way to mark the View step done after the modal closes.
  const [viewedId, setViewedId] = useState<string | null>(null);

  // The View modal. `url` is an object URL we own — revoked on close, or the blob
  // survives for the life of the tab.
  const [viewing, setViewing] = useState<PublishedDocument | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  const load = useCallback(async (keepCategory?: string | null) => {
    const t0 = performance.now();
    setLoading(true);
    try {
      const cats = await documentsApi.categories();
      setCategories(cats);
      // Keep the open category when it still has something readable — a Refresh should
      // not throw the reader back to the first category. It CAN disappear (its last
      // document retires), so fall back to the first one rather than showing nothing.
      // The nav leaf wins on first load; after that the user's own rail pick does.
      const wanted = keepCategory ?? activeCat ?? initialCategoryId;
      const next = cats.some(c => c.id === wanted) ? wanted! : cats[0]?.id ?? null;
      setActiveCat(next);
      setRows(next ? await documentsApi.byCategory(next) : []);
    } finally {
      setLoading(false);
      setLoadMs(performance.now() - t0);
    }
    // activeCat is read through the `keepCategory ?? activeCat` fallback only; adding it
    // to the deps would rebuild `load` on every category click and re-trigger the mount
    // effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCategoryId]);

  useEffect(() => { void load(); }, [load]);

  const closeViewer = useCallback(() => {
    setViewing(null);
    setFileUrl(null);
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  }, []);

  // Revoke on unmount too — closing the form while the modal is open must not leak.
  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  const view = useCallback(async (doc: PublishedDocument) => {
    setViewing(doc);
    setFileUrl(null);
    try {
      const url = await documentsApi.fileUrl(doc.oid);
      urlRef.current = url;
      setFileUrl(url);
      setViewedId(doc.oid);
    } catch {
      // The server 404s when the document has no current file — which can happen between
      // the grid loading and the click (its last active attachment was just removed).
      // Say so and close, rather than leaving an empty frame open.
      setViewing(null);
      notify.error('No current file for this document. Refresh and try again.');
    }
  }, [notify]);

  const columns = useMemo<AppColumn<PublishedDocument>[]>(() => [
    { field: 'name', header: 'Name', width: 400, text: r => r.name ?? '' },
    { field: 'subName', header: 'Sub Name', width: 150, text: r => r.subName ?? '' },
    { field: 'description', header: 'Description', width: 260, text: r => r.description ?? '' },
    { field: 'versionNumber', header: 'Version Number', width: 130, text: r => r.versionNumber ?? '' },
    { field: 'revNumber', header: 'Rev Number', width: 110, text: r => r.revNumber ?? '' },
    {
      // Legacy captions this column "View" over the computed `Download` property and
      // renders it blue + underlined. The icon is 👁 Preview, NOT ⬇️ Load: the standard
      // reserves ⬇️ for opening a record and 👁 for opening a document, and records a
      // past bug from using one for the other.
      field: 'view', header: 'View', width: 90, text: () => 'View',
      render: r => (
        <button type="button" className="doc-view" onClick={() => void view(r)}
          title={`Open ${r.name ?? 'document'}`}>
          <i className="ti ti-eye" aria-hidden="true" /> View
        </button>
      ),
    },
  ], [view]);

  const stepperSteps = useMemo(
    () => documentSteps({ activeCat, selected, viewing: !!viewing, viewedId }),
    [activeCat, selected, viewing, viewedId]);

  const activeName = categories.find(c => c.id === activeCat)?.name ?? '';

  return (
    <div className="doc-root">
      {/* The shared Dynamic Island, portalled into the AppShell top-bar slot
          (BO UI Standard §5 placement). See the Step Island note at the top of
          this file for why a Class E screen carries one. */}
      <TopbarStepper steps={stepperSteps} />

      {/* The SHARED page-header wrapper every ModuleListView form uses:
          `.bl-top` supplies the horizontal gutter (20px, 12px on narrow — the same
          one `.doc-body` below already uses, so header and grid line up) and
          `.dp-page-hdr` the vertical breathing room (16px above, 10px below). A
          bespoke page has to nest these itself; without them the breadcrumb and
          toolbar sat flush against the content edge, which is what the user
          compared against Custom Reporting on 2026-10-12. Reused, never
          re-invented as per-screen padding (standing rule 9). */}
      <div className="bl-top">
        <div className="dp-page-hdr">
      {/* Breadcrumb — the shared `dp-breadcrumb`, Home › group › form. ModuleListView
          renders exactly this for a READ-ONLY list module and suppresses it only for
          CRUD ones ("the topbar already shows the path", 2026-07-02). This screen is
          Class E, so it is on the SHOWING side of that rule — but a bespoke page has
          to render it itself. (User report 2026-10-12: missing here.) */}
      <div className="dp-breadcrumb">
        <a onClick={onHome} style={{ cursor: 'pointer' }}>Home</a>
        <i className="ti ti-chevron-right" />
        <span>Document Management</span>
        <i className="ti ti-chevron-right" />
        {/* Opened from a CATEGORY nav leaf, the crumb mirrors the nav path
            (… › Documents › SOPs); opened on the plain entry it ends at
            Documents, as before. */}
        {initialCategoryId ? (<>
          <span>Documents</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">{activeName || '…'}</span>
        </>) : <span className="dp-bc-active">Documents</span>}
      </div>

      {/* The Audit chip points at "Document Creator", NOT a "Documents" FormName:
          this screen writes nothing (Class E), so a chip on its own name would open an
          empty log every time. The meaningful history of these records — created,
          retired, reactivated — is the document's, and keeping it under ONE FormName is
          the same call made for Retired Document (and the Reporting-lifecycle
          precedent). */}
      <FormTitle pageId="documents" loadMs={loadMs} loading={loading} auditFormName="Document Creator" />

      <AppToolbar
        right={
          // Project Notes (BO UI Standard §8) — mandatory on every BO screen. This
          // is a LIST with no detail view, so the note is anchored to the SELECTED
          // row; with nothing selected the button says so rather than disappearing.
          // In the `right` slot, so it sits with the Loaded + Audit cluster at the
          // END of the bar as on every other form, not stranded at the far left
          // (user report 2026-10-12: "it always be with audit button").
          // Read-only here: a reader annotating the library is an authoring action,
          // and this screen is Class E — notes are written on Document Creator.
          <ProjectNotesButton
            recordId={selected}
            baseUrl={selected ? `/api/v1/documents/${selected}/project-notes` : null}
            recordLabel={rows.find(r => r.oid === selected)?.name ?? ''}
            recordSubLabel={activeName}
            businessObject="Document Creator"
            readOnly
            disabledReason="Select a document to read its Project Notes"
          />
        }
        onRefresh={() => { void load(activeCat); }}
        refreshing={loading}
        exportData={{
          filename: `Documents-${activeName || 'all'}`,
          headers: ['Name', 'Sub Name', 'Description', 'Version Number', 'Rev Number'],
          rows: rows.map(r => [r.name ?? '', r.subName ?? '', r.description ?? '',
            r.versionNumber ?? '', r.revNumber ?? '']),
        }}
      />
        </div>
      </div>

      {/* The grid alone. The category RAIL that used to sit left of it was
          removed on owner request 2026-10-12 ("in the list view remove the
          document names because we show that in the navigation") — once each
          DocumentCategory became its own sidebar leaf the rail repeated the
          navigation, and the chosen category already shows in the top bar, the
          breadcrumb and the grid caption. Switching category is now the
          sidebar's job, which is also how G5 works. `categories` is still
          fetched: the Step Island and the grid caption read it, and `load()`
          needs it to resolve which category to open. */}
      <div className="doc-body">
        <div className="doc-grid">
          <AppDataGrid
            rows={rows}
            columns={columns}
            getRowId={r => r.oid}
            selectedRowId={selected}
            onRowClick={r => setSelected(r.oid)}
            onRowDoubleClick={r => { void view(r); }}
            loading={loading}
            maximizable
            gridTitle={activeName}
            exportFilename={`Documents-${activeName || 'all'}`}
            defaultPageSize={20}
            emptyText="No documents in this category."
          />
        </div>
      </div>

      <AppDialog
        open={!!viewing}
        title={viewing?.name ?? 'Document'}
        icon="ti-file-text"
        width={900}
        maximizable
        onClose={closeViewer}
      >
        {fileUrl
          ? <iframe className="doc-pdf" src={fileUrl} title={viewing?.name ?? 'Document'} />
          : <p className="doc-pdf-loading">Opening…</p>}
      </AppDialog>
    </div>
  );
}

export default DocumentsPage;
